import {action, computed, observable, runInAction} from 'mobx';

import {Tracker} from '@udemy/event-tracking';
import {ToasterStore as toasterStore} from '@udemy/react-messaging-components';

import {ApiClientStore} from './api-client-store';
import {ApiClientCreateCancelled, ApiClientCreated} from '../lib/events';

export type CreateApiClientForm = {
    clientName: string;
    scopes: string[];
    isSubmitting: boolean;
};

export type CreateApiClientResponse = Pick<CreateApiClientForm, 'clientName' | 'scopes'> & {
    clientId: string;
    clientSecret: string;
};

export class CreateModalStore {
    constructor(private apiClientStore: ApiClientStore) {}

    @observable isOpen = false;
    @observable form: CreateApiClientForm = {
        clientName: '',
        scopes: [],
        // ToS accepted is only relevant to the UI and not the form
        isSubmitting: false,
    };
    @observable isToSAccepted = false;
    @observable createdClient: CreateApiClientResponse | null = null;
    @observable activeStep: 'clientName' | 'scopeSelection' | 'clientCreated' = 'clientName';
    @observable permissions = [
        {
            namespace: 'course',
            description: 'Access to all course options',
            checked: false,
            scopes: [
                {
                    name: 'course:read',
                    description: 'Access to course and lecture information',
                    checked: false,
                },
                {
                    name: 'course:enroll',
                    description: 'Enables the enrollment of a course',
                    checked: false,
                },
            ],
        },
        {
            namespace: 'lab',
            description: 'Access to all lab options',
            checked: false,
            scopes: [
                {
                    name: 'lab:read',
                    description: 'Access to lab information',
                    checked: false,
                },
            ],
        },
        {
            namespace: 'open-badges',
            description: 'Access to all open-badges options',
            checked: false,
            scopes: [
                {
                    name: 'badgeclass:read',
                    description: 'Access to badges displayed for certification preparation',
                    checked: false,
                },
                {
                    name: 'badgeassertion:read',
                    description: 'Access to badge data on the organization account',
                    checked: false,
                },
            ],
        },
        {
            namespace: 'mcp',
            description: 'Access to all MCP options',
            checked: false,
            scopes: [
                {
                    name: 'mcp:read',
                    description: 'Access to MCP read scope operations',
                    checked: false,
                },
                {
                    name: 'mcp:write',
                    description: 'Access to MCP write scope operations',
                    checked: false,
                },
            ],
        },
    ];

    @computed get scopesList() {
        return this.permissions
            .map((permission) => {
                return permission.scopes
                    .filter((scope) => scope.checked)
                    .map((scope) => scope.name);
            })
            .flat();
    }

    @action open() {
        this.isOpen = true;
        this.activeStep = 'clientName';
        this.form = {clientName: '', scopes: [], isSubmitting: false};
        this.createdClient = null;
        this.isToSAccepted = false;
    }

    @action close() {
        this.isOpen = false;

        // No need to track on closing modal in success step or other ones
        if (this.activeStep === 'clientName') {
            Tracker.publishEvent(new ApiClientCreateCancelled('modal.client_name'));
        }
        if (this.activeStep === 'scopeSelection') {
            Tracker.publishEvent(new ApiClientCreateCancelled('modal.scope_select'));
        }
    }

    @action setClientName(clientName: string) {
        this.form.clientName = clientName;
    }

    @action setToSAccepted(isToSAccepted: boolean) {
        this.isToSAccepted = isToSAccepted;
    }

    @action toScopeSelection() {
        this.activeStep = 'scopeSelection';
    }

    @action async save() {
        this.form.isSubmitting = true;
        this.form.scopes = this.scopesList;

        try {
            const client = await this.apiClientStore.addApiClient(this.form);
            runInAction(() => {
                this.form.isSubmitting = false;
                this.createdClient = {
                    clientId: client.clientId,
                    clientName: client.clientName,
                    clientSecret: client.secret,
                    scopes: client.scopes,
                };
                this.activeStep = 'clientCreated';
            });

            toasterStore.addAlertBannerToast({
                title: `You have successfully created ${client.clientName}`,
                udStyle: 'success',
                ctaText: 'See API',
                actionButtonProps: {
                    componentClass: 'a',
                    href: process.env.DEPLOY_BASE_PATH + '/api-clients/' + client.clientId,
                },
            });

            Tracker.publishEvent(new ApiClientCreated(client.clientId));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            // We don't have error handling so we just close the modal so they'll try again
            this.close();
            toasterStore.addAlertBannerToast(
                {
                    title: 'Unable to create API client',
                    udStyle: 'error',
                    showCta: false,
                    dismissButtonProps: false,
                },
                {autoDismiss: true, autoDismissTimeout: 5000},
            );
        }
    }

    // Selects a namespace and all underlying scopes (e.g. course -> course:read, course:enroll)
    @action selectScopeNamespace(permission: string, checked: boolean) {
        const namespace = this.permissions.find((scope) => scope.namespace === permission);
        if (namespace) {
            namespace.checked = checked;
            namespace.scopes.forEach((scope) => (scope.checked = checked));
        }
    }

    // Selects a single scope from namespace, sets namespace to checked if all scopes are checked
    @action selectScope(permission: string, scope: string, checked: boolean) {
        const namespace = this.permissions.find((scope) => scope.namespace === permission);
        if (namespace) {
            const targetScope = namespace.scopes.find((s) => s.name === scope);
            if (targetScope) {
                targetScope.checked = checked;
            }
            // Keep namespace checked if all scopes are checked
            namespace.checked = namespace.scopes.every((s) => s.checked);
        }
    }
}
